import { Table, Button, Modal, Input, Tag, message, Row, Col, Typography, Select } from "antd";
import { useState, useContext, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { CheckOutlined, CloseOutlined, SearchOutlined, EyeOutlined } from "@ant-design/icons";

const { Title } = Typography;

export default function HodChange() {
    const { authData, backUrl } = useContext(AuthContext);
    const [requests, setRequests] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [filters, setFilters] = useState({ status: "PENDING", year: null });
    const [counts, setCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0 });
    const [searchText, setSearchText] = useState("");

    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [adminRemarks, setAdminRemarks] = useState("");
    const [selectedRecord, setSelectedRecord] = useState(null);

    const [isSubsModalVisible, setIsSubsModalVisible] = useState(false);
    const [subsRecord, setSubsRecord] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ✅ Fetch HOD transfer requests
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append("status", filters.status);
            if (searchText.trim()) params.append("search", searchText.trim());
            
            const res = await axios.get(`${backUrl}/api/hr/transfer/list/?${params.toString()}`, {
                headers: { Authorization: `Bearer ${authData.access}` },
            });
            setRequests(res.data.results || res.data || []);
        } catch (error) {
            console.error("Error fetching HOD transfer requests:", error.response || error.message);
            message.error("Failed to load HOD transfer requests.");
        } finally {
            setLoading(false);
        }
    }, [backUrl, authData, filters.status, searchText]);

    const fetchCounts = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/hod-transfer-status-counts/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            const countsMap = { PENDING: 0, APPROVED: 0, REJECTED: 0 };
            res.data.forEach(item => {
                countsMap[item.title.toUpperCase()] = item.count;
            });
            setCounts(countsMap);
        } catch (err) {
            console.error("Failed to fetch counts:", err);
        }
    };

    useEffect(() => {
        fetchData();
        fetchCounts();
    }, [fetchData, filters.status]);

    // ✅ Status colors
    const statusColors = {
        PENDING: "orange",
        APPROVED: "green",
        REJECTED: "red",
        CANCELLED: "gray",
    };

    // ✅ Handle Approve
    const handleApprove = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/hod-replacement/approve/${selectedRecord.id}/`,
                { action: "APPROVE", approver_reason: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "HOD transfer request approved successfully.");
            setIsApproveModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
            await fetchCounts();
        } catch (error) {
            console.error("Error approving request:", error);
            message.error(error.response?.data?.message || "Failed to approve request.");
        }
    };

    // ✅ Handle Reject
    const handleReject = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/hod-replacement/approve/${selectedRecord.id}/`,
                { action: "REJECT", reason: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "HOD transfer request rejected.");
            setIsRejectModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
            await fetchCounts();
        } catch (error) {
            console.error("Error rejecting request:", error);
            message.error(error.response?.data?.message || "Failed to reject request.");
        }
    };

    // ✅ Columns
    const columns = [

        {
            title: "Outgoing HOD",
            key: "outgoing_hod",
            render: (_, record) => record.outgoing_hod_name || record.outgoing_hod || "-",
        },
        {
            title: "Incoming HOD",
            key: "incoming_hod",
            render: (_, record) => record.incoming_hod_name || record.incoming_hod || "-",
        },
        {
            title: "Requested By",
            key: "requesting_admin",
            render: (_, record) => record.requesting_admin_name || record.requesting_admin || "-",
        },
        {
            title: "Transferred Employees",
            key: "target_employees",
            render: (_, record) => {
                const employees = record.target_employees || [];
                const count = record.total_count ?? employees.length;
                if (count === 0) return "-";
                return (
                    <Button

                        style={{ padding: 0 }}
                        onClick={() => {
                            setSubsRecord(record);
                            setIsSubsModalVisible(true);
                        }}
                    >
                        ({count}) employees
                    </Button>
                );
            },
        },
        {
            title: "Requested On",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Approved On",
            dataIndex: "processed_at",
            key: "processed_at",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Remarks",
            key: "approver_reason",
            render: (_, record) => record.approver_reason || "-",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <Tag color={statusColors[text] || "default"}>{text || "-"}</Tag>
            ),
        },
        ...(authData?.employee?.is_superuser
            ? [{
                title: "Actions",
                key: "actions",
                render: (_, record) => {
                    if (record.status !== "PENDING") return "-";

                    return (
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button
                                type="text"
                                style={{ color: "green" }}
                                icon={<CheckOutlined />}
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setIsApproveModalVisible(true);
                                }}
                            />
                            <Button
                                type="text"
                                danger
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setIsRejectModalVisible(true);
                                }}
                            />
                        </div>
                    );
                },
            }]
            : []),
    ];
    
    // ✅ Filter logic (Year filter still works locally if needed)
    const years = Array.from(
        new Set(
            requests
                .filter((item) => item.created_at)
                .map((item) => dayjs(item.created_at).year())
        )
    ).sort((a, b) => b - a);

    const filteredData = requests.filter((item) => {
        const date = dayjs(item.created_at);
        const matchYear = filters.year ? date.year() === filters.year : true;
        return matchYear;
    });

    return (
        <>
            <Title level={4} style={{ 
                color: "#0e497b", 
                marginBottom: 20, 
                textAlign: "center",
                textTransform: "uppercase" 
            }}>
                Change HOD Requests
            </Title>
            
            {/* ✅ Status Tabs (Button Group style) */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Button
                    type={filters.status === "PENDING" ? "primary" : "default"}
                    onClick={() => setFilters(prev => ({ ...prev, status: "PENDING" }))}
                    style={{
                        marginRight: "10px",
                        backgroundColor: filters.status === "PENDING" ? "#faad14" : undefined,
                        borderColor: filters.status === "PENDING" ? "#faad14" : undefined,
                        color: filters.status === "PENDING" ? "white" : undefined,
                        minWidth: "120px"
                    }}
                >
                    Pending ({counts.PENDING})
                </Button>
                <Button
                    type={filters.status === "APPROVED" ? "primary" : "default"}
                    onClick={() => setFilters(prev => ({ ...prev, status: "APPROVED" }))}
                    style={{
                        marginRight: "10px",
                        backgroundColor: filters.status === "APPROVED" ? "#4CAF50" : undefined,
                        borderColor: filters.status === "APPROVED" ? "#4CAF50" : undefined,
                        color: filters.status === "APPROVED" ? "white" : undefined,
                        minWidth: "120px"
                    }}
                >
                    Approved ({counts.APPROVED})
                </Button>
                <Button
                    type={filters.status === "REJECTED" ? "primary" : "default"}
                    onClick={() => setFilters(prev => ({ ...prev, status: "REJECTED" }))}
                    style={{
                        marginRight: "10px",
                        backgroundColor: filters.status === "REJECTED" ? "#f5222d" : undefined,
                        borderColor: filters.status === "REJECTED" ? "#f5222d" : undefined,
                        color: filters.status === "REJECTED" ? "white" : undefined,
                        minWidth: "120px"
                    }}
                >
                    Rejected ({counts.REJECTED})
                </Button>
            </div>

            {/* Filter Section (Search) */}
            <div
                style={{
                    background: "#ffffff",
                    padding: "15px",
                    borderRadius: "10px",
                    marginBottom: "15px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                }}
            >
                <Row gutter={[16, 16]} justify="center">
                    <Col xs={24} sm={18} md={10}>
                        <Input
                            placeholder="Employee Name / Code"
                            prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                            style={{ width: "100%" }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Select
                            placeholder="Filter by Year"
                            style={{ width: "100%" }}
                            allowClear
                            value={filters.year}
                            onChange={(value) => setFilters((prev) => ({ ...prev, year: value }))}
                        >
                            {years.map((year) => (
                                <Select.Option key={year} value={year}>
                                    {year}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            </div>

            <Modal
                title="Approve HOD Transfer"
                open={isApproveModalVisible}
                onOk={handleApprove}
                onCancel={() => {
                    setIsApproveModalVisible(false);
                    setAdminRemarks("");
                    setSelectedRecord(null);
                }}
                okText="Approve"
                cancelText="Close"
                okButtonProps={{ style: { backgroundColor: "green", borderColor: "green" } }}
            >
                <p>
                    Are you sure you want to <strong>approve</strong> this HOD transfer request
                    {selectedRecord?.id ? ` (ID: #${selectedRecord.id})` : ""}?
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason for approval"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            {/* ✅ Reject Modal */}
            <Modal
                title="Reject HOD Transfer"
                open={isRejectModalVisible}
                onOk={handleReject}
                onCancel={() => {
                    setIsRejectModalVisible(false);
                    setAdminRemarks("");
                    setSelectedRecord(null);
                }}
                okText="Reject"
                cancelText="Close"
                okButtonProps={{ danger: true }}
            >
                <p>
                    Are you sure you want to <strong>reject</strong> this HOD transfer request
                    {selectedRecord?.id ? ` (ID: #${selectedRecord.id})` : ""}?
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason for rejection"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            {/* ✅ Transferred Employees Modal */}
            <Modal
                title={`Transferred Employees ${subsRecord ? `(${subsRecord.total_count ?? subsRecord.target_employees?.length ?? 0})` : ""}`}
                open={isSubsModalVisible}
                onCancel={() => {
                    setIsSubsModalVisible(false);
                    setSubsRecord(null);
                }}
                footer={
                    <Button onClick={() => { setIsSubsModalVisible(false); setSubsRecord(null); }}>
                        Close
                    </Button>
                }
            >
                {subsRecord?.target_employees?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "8px 0" }}>
                        {subsRecord.target_employees.map((emp, i) => (
                            <div
                                key={i}
                                style={{
                                    background: "#f8fafd",
                                    border: "1px solid #dde3ed",
                                    borderRadius: 8,
                                    padding: "10px 14px",
                                }}
                            >
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.first_name}</div>
                                <div style={{ fontSize: 12, color: "#8fa3bc" }}>{emp.emp_code}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p style={{ color: "#8fa3bc", textAlign: "center" }}>No employees listed.</p>
                )}
            </Modal>

            {/* ✅ Table */}
            <Table
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                rowKey={(record) => record.id}
                pagination={{ pageSize: 10 }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />
        </>
    );
}